import { useEffect, useRef } from "react";
import { getProFrame } from "@/lib/simulation/professionalSequence";

interface Props { elapsed: number; homeCode?: string; awayCode?: string; }

/** Lightweight 2D 22-player tactical mini-map, canvas-rendered for performance. */
export function TacticalMiniMap({ elapsed, homeCode = "HOME", awayCode = "AWAY" }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  const elapsedRef = useRef(elapsed);
  useEffect(() => { elapsedRef.current = elapsed; }, [elapsed]);

  useEffect(() => {
    const cvs = ref.current; if (!cvs) return;
    const ctx = cvs.getContext("2d"); if (!ctx) return;
    let raf = 0;
    const dpr = Math.min(window.devicePixelRatio, 2);
    const resize = () => {
      const r = cvs.getBoundingClientRect();
      cvs.width = r.width * dpr; cvs.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      const w = cvs.clientWidth, h = cvs.clientHeight;
      ctx.clearRect(0, 0, w, h);
      // Field
      ctx.fillStyle = "oklch(0.25 0.06 145)";
      ctx.fillRect(0, 0, w, h);
      // Grass stripes for sharpness
      ctx.fillStyle = "oklch(0.28 0.07 145 / 0.45)";
      const stripes = 8;
      for (let i = 0; i < stripes; i++) {
        if (i % 2 === 0) continue;
        ctx.fillRect((w / stripes) * i, 4, w / stripes, h - 8);
      }
      ctx.strokeStyle = "rgba(255,255,255,0.45)";
      ctx.lineWidth = 1;
      ctx.strokeRect(4, 4, w - 8, h - 8);
      ctx.beginPath(); ctx.moveTo(w / 2, 4); ctx.lineTo(w / 2, h - 4); ctx.stroke();
      ctx.beginPath(); ctx.arc(w / 2, h / 2, Math.min(w, h) * 0.12, 0, Math.PI * 2); ctx.stroke();
      // Areas
      const ah = h * 0.55;
      ctx.strokeRect(4, (h - ah) / 2, w * 0.16, ah);
      ctx.strokeRect(w - 4 - w * 0.16, (h - ah) / 2, w * 0.16, ah);

      const frame = getProFrame(elapsedRef.current, 0.05, "snapshot");
      const toScreen = (x: number, y: number) => ({
        sx: ((x + 50) / 100) * (w - 12) + 6,
        sy: ((y + 32) / 64) * (h - 12) + 6,
      });

      // Danger zone overlay (ARG box, right side) when ball is in final third
      if (frame.ball.x > 16) {
        const dz = toScreen(32, -22);
        const dzEnd = toScreen(50, 22);
        const grad = ctx.createLinearGradient(dz.sx, 0, dzEnd.sx, 0);
        grad.addColorStop(0, "rgba(225,6,0,0)");
        grad.addColorStop(1, "rgba(225,6,0,0.32)");
        ctx.fillStyle = grad;
        ctx.fillRect(dz.sx, dz.sy, dzEnd.sx - dz.sx, dzEnd.sy - dz.sy);
      }

      // Identify ball carrier (nearest player)
      let carrierId = -1; let bestD = Infinity;
      frame.players.forEach((p) => {
        const d = Math.hypot(p.x - frame.ball.x, p.y - frame.ball.y);
        if (d < bestD) { bestD = d; carrierId = p.id; }
      });

      // Players
      frame.players.forEach((p) => {
        const { sx, sy } = toScreen(p.x, p.y);
        const teamColor = p.team === "MEX" ? "#10b981" : "#60a5fa";
        // soft outline for sharpness
        ctx.strokeStyle = "rgba(0,0,0,0.55)";
        ctx.lineWidth = 1.2;
        ctx.fillStyle = teamColor;
        ctx.beginPath();
        ctx.arc(sx, sy, p.role === "GK" ? 3.8 : 3.0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        if (p.sprinting) {
          ctx.strokeStyle = "rgba(250,204,21,0.85)";
          ctx.lineWidth = 1.5;
          ctx.beginPath(); ctx.arc(sx, sy, 5.5, 0, Math.PI * 2); ctx.stroke();
        }
        if (p.id === carrierId && bestD < 5) {
          ctx.strokeStyle = "rgba(255,255,255,0.95)";
          ctx.lineWidth = 1.6;
          ctx.beginPath(); ctx.arc(sx, sy, 6.5, 0, Math.PI * 2); ctx.stroke();
        }
      });
      // Ball
      const { sx, sy } = toScreen(frame.ball.x, frame.ball.y);
      // HARD LOCK FIELD STABILITY — stable ball marker, no perpetual breathing.
      const pulse = frame.inDanger ? 5.2 : 4.5;
      ctx.fillStyle = frame.inDanger ? "rgba(250,204,21,0.25)" : "rgba(250,204,21,0.18)";
      ctx.beginPath(); ctx.arc(sx, sy, pulse + 3, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.95)";
      ctx.lineWidth = 1.4;
      ctx.beginPath(); ctx.arc(sx, sy, pulse, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = "#ffffff";
      ctx.beginPath(); ctx.arc(sx, sy, 2.4, 0, Math.PI * 2); ctx.fill();

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);

  return (
    <div className="relative overflow-hidden rounded-xl border border-border/60 glass">
      <div className="flex items-center justify-between px-2 pt-1.5 pb-1 text-[9px] font-bold tracking-widest text-muted-foreground">
        <span>MINI-MAP · 22</span>
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#10b981]" />{homeCode}</span>
          <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#60a5fa]" />{awayCode}</span>
        </span>
      </div>
      <canvas ref={ref} className="block h-[110px] w-full" aria-label="Tactical mini-map" />
    </div>
  );
}